interface ResultsSummaryProps {
  score: number;
  total: number;
  subtopicBreakdown: {
    subtopic: string;
    correct: number;
    total: number;
  }[];
  onReviewAnswers: () => void;
  onRetakeTest: () => void;
}

export default function ResultsSummary({
  score,
  total,
  subtopicBreakdown,
  onReviewAnswers,
  onRetakeTest,
}: ResultsSummaryProps) {
  const percentage = Math.round((score / total) * 100);
  const incorrect = total - score;

  const getPerformanceMessage = () => {
    if (percentage >= 90) return { text: 'Outstanding!', color: 'text-emerald-600', emoji: '🎉' };
    if (percentage >= 80) return { text: 'Excellent Work!', color: 'text-emerald-600', emoji: '🌟' };
    if (percentage >= 70) return { text: 'Good Job!', color: 'text-teal-600', emoji: '👍' };
    if (percentage >= 60) return { text: 'Keep Practicing!', color: 'text-yellow-600', emoji: '💪' };
    return { text: 'Review & Try Again', color: 'text-orange-600', emoji: '📚' };
  };

  const performance = getPerformanceMessage();

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="text-6xl mb-4">{performance.emoji}</div>
        <h2 className={`text-4xl font-bold mb-2 ${performance.color}`}>
          {performance.text}
        </h2>
        <p className="text-gray-600 text-lg">You've completed the practice test</p>
      </div>

      {/* Score Card */}
      <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl p-8 text-white mb-6 shadow-lg">
        <div className="text-center mb-6">
          <div className="text-7xl font-bold mb-2">{percentage}%</div>
          <div className="text-xl opacity-90">Your Score</div>
        </div>
        
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white/20 rounded-lg p-4 text-center backdrop-blur-sm">
            <div className="text-3xl font-bold">{score}</div>
            <div className="text-sm opacity-90">Correct</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4 text-center backdrop-blur-sm">
            <div className="text-3xl font-bold">{incorrect}</div>
            <div className="text-sm opacity-90">Incorrect</div>
          </div>
          <div className="bg-white/20 rounded-lg p-4 text-center backdrop-blur-sm">
            <div className="text-3xl font-bold">{total}</div>
            <div className="text-sm opacity-90">Total</div>
          </div>
        </div>
      </div>

      {/* Subtopic Breakdown */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-6">
        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-bar-chart-line text-emerald-600"></i>
          Performance by Subtopic
        </h3>
        <div className="space-y-4">
          {subtopicBreakdown.map((item, index) => {
            const subtopicPercentage = (item.correct / item.total) * 100;
            return (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-700">{item.subtopic}</span>
                  <span className="text-sm font-semibold text-gray-600">
                    {item.correct}/{item.total} ({Math.round(subtopicPercentage)}%)
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      subtopicPercentage >= 80
                        ? 'bg-emerald-500'
                        : subtopicPercentage >= 60
                        ? 'bg-yellow-500'
                        : 'bg-red-500'
                    }`}
                    style={{ width: `${subtopicPercentage}%` }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={onReviewAnswers}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-4 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <i className="ri-file-list-3-line text-xl"></i>
          Review All Answers
        </button>
        <button
          onClick={onRetakeTest}
          className="flex-1 bg-white hover:bg-gray-50 text-gray-800 font-semibold py-4 rounded-lg border-2 border-gray-300 transition-colors duration-200 flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <i className="ri-refresh-line text-xl"></i>
          Retake Test
        </button>
      </div>
    </div>
  );
}